import { useState, useMemo } from "react";
import {
  Settings,
  Edit2,
  Check,
  X,
  Play,
  Pause,
  SkipBack,
  Repeat,
  Volume2,
  VolumeX,
  ChevronsUpDown,
  ChevronRight,
  ChevronDown,
  Monitor,
  Plus,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import type {
  AtemState,
  AtemInput,
  AtemMixEffect,
  AtemTransitionStyle,
  AtemUpstreamKeyer,
  AtemDownstreamKeyer,
  AtemColorGenerator,
  AtemAudioChannel,
  AtemFairlightInput,
  AtemFairlightSource,
  AtemAuxOutput,
  AtemAudioRoutingOutput,
  AtemAudioRoutingChannelPair,
  AtemExternalPortType,
  AtemFairlightInputConfiguration,
  AtemMediaPlayer,
  AtemSuperSource,
  AtemSuperSourceBox,
  AtemMultiView,
  AtemMacro,
} from "@production-hub/shared";

/* ─────────────────────────────────────────────────────────── */
/* ATEM Command types                                          */
/* ─────────────────────────────────────────────────────────── */

export type AtemCommand =
  | {
      action: "label_input";
      index: number;
      shortName: string;
      longName: string;
    }
  | { action: "set_program"; meIndex: number; inputIndex: number }
  | { action: "set_preview"; meIndex: number; inputIndex: number }
  | { action: "cut"; meIndex: number }
  | { action: "auto"; meIndex: number }
  | {
      action: "set_transition_style";
      meIndex: number;
      style: AtemTransitionStyle;
    }
  | { action: "set_transition_rate"; meIndex: number; rate: number }
  | { action: "fade_to_black"; meIndex: number }
  | { action: "set_ftb_rate"; meIndex: number; rate: number }
  | { action: "set_aux"; auxIndex: number; inputIndex: number }
  | {
      action: "label_aux";
      auxIndex: number;
      shortName: string;
      longName: string;
    }
  | {
      action: "label_fairlight_input";
      inputIndex: number;
      shortName: string;
      longName: string;
    }
  | {
      action: "set_usk_on_air";
      meIndex: number;
      keyerIndex: number;
      onAir: boolean;
    }
  | {
      action: "set_usk_fill_source";
      meIndex: number;
      keyerIndex: number;
      source: number;
    }
  | {
      action: "set_usk_key_source";
      meIndex: number;
      keyerIndex: number;
      source: number;
    }
  | {
      action: "set_usk_type";
      meIndex: number;
      keyerIndex: number;
      type: AtemUpstreamKeyer["type"];
    }
  | { action: "set_dsk_on_air"; dskIndex: number; onAir: boolean }
  | { action: "set_dsk_tie"; dskIndex: number; tie: boolean }
  | { action: "set_dsk_auto"; dskIndex: number }
  | { action: "set_dsk_fill_source"; dskIndex: number; source: number }
  | { action: "set_dsk_key_source"; dskIndex: number; source: number }
  | { action: "set_dsk_rate"; dskIndex: number; rate: number }
  | {
      action: "set_color_generator";
      index: number;
      hue: number;
      saturation: number;
      luminance: number;
    }
  | {
      action: "set_audio_mix_option";
      channelIndex: number;
      mixOption: AtemAudioChannel["mixOption"];
    }
  | { action: "set_audio_gain"; channelIndex: number; gain: number }
  | { action: "set_audio_balance"; channelIndex: number; balance: number }
  | { action: "set_audio_master_gain"; gain: number }
  | {
      action: "set_media_player_source";
      mpIndex: number;
      sourceType: "still" | "clip";
      sourceIndex: number;
    }
  | { action: "set_media_player_playing"; mpIndex: number; playing: boolean }
  | { action: "set_media_player_loop"; mpIndex: number; loop: boolean }
  | {
      action: "set_supersource_box";
      ssIndex: number;
      boxIndex: number;
      updates: Partial<AtemSuperSourceBox>;
    }
  | {
      action: "set_supersource_art";
      ssIndex: number;
      fillSource: number;
      keySource: number;
    }
  | {
      action: "set_multiview_window_source";
      mvIndex: number;
      windowIndex: number;
      source: number;
    }
  | { action: "set_multiview_layout"; mvIndex: number; layout: number }
  | { action: "set_multiview_vu_meters"; mvIndex: number; enabled: boolean }
  | {
      action: "set_multiview_program_preview_swapped";
      mvIndex: number;
      swapped: boolean;
    }
  | { action: "set_audio_monitor_gain"; gain: number }
  | { action: "set_audio_monitor_mute"; mute: boolean }
  | { action: "set_audio_monitor_solo"; solo: boolean; inputIndex: number }
  | { action: "set_audio_headphone_gain"; gain: number }
  | { action: "set_audio_headphone_mute"; mute: boolean }
  | {
      action: "set_fairlight_input_gain";
      inputIndex: number;
      sourceId: number;
      gain: number;
    }
  | {
      action: "set_fairlight_input_fader";
      inputIndex: number;
      sourceId: number;
      faderGain: number;
    }
  | {
      action: "set_fairlight_input_balance";
      inputIndex: number;
      sourceId: number;
      balance: number;
    }
  | {
      action: "set_fairlight_input_mix_option";
      inputIndex: number;
      sourceId: number;
      mixOption: "off" | "on" | "afv";
    }
  | {
      action: "set_fairlight_input_configuration";
      inputIndex: number;
      configuration: AtemFairlightInputConfiguration;
    }
  | {
      action: "set_audio_routing_channel_pair";
      outputIndex: number;
      pairIndex: number;
      sourceInputIndex: number;
      sourceId: number;
      enabled: boolean;
    }
  | { action: "run_macro"; macroIndex: number }
  | { action: "stop_macro" };

/* ─────────────────────────────────────────────────────────── */
/* Main Panel                                                   */
/* ─────────────────────────────────────────────────────────── */

interface AtemPanelProps {
  state: AtemState;
  onCommand?: (command: AtemCommand) => void;
  /** @deprecated use onCommand instead */
  onLabelInput?: (index: number, shortName: string, longName: string) => void;
  isLoading?: boolean;
}

export const AtemPanel = ({
  state,
  onCommand,
  onLabelInput,
  isLoading,
}: AtemPanelProps) => {
  const [selectedME, setSelectedME] = useState(0);

  const send = (cmd: AtemCommand) => {
    if (onCommand) onCommand(cmd);
    // Backwards compatibility
    if (cmd.action === "label_input" && onLabelInput) {
      onLabelInput(cmd.index, cmd.shortName, cmd.longName);
    }
  };

  const sdiInputs = useMemo(
    () => state.inputs.filter((i) => i.index < 1000),
    [state.inputs],
  );
  const allSources = state.inputs;

  return (
    <div className="space-y-4">
      {/* Device Info */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{state.model}</h3>
          <p className="text-sm text-muted-foreground">
            {sdiInputs.length} inputs / {state.mixEffects.length} M/E
            {state.auxiliaries.length > 0 &&
              ` / ${state.auxiliaries.length} Aux`}
            {state.downstreamKeyers?.length > 0 &&
              ` / ${state.downstreamKeyers.length} DSK`}
          </p>
        </div>
        <Badge variant="outline" className="gap-1">
          <Settings className="h-3 w-3" />
          ATEM
        </Badge>
      </div>

      <Tabs defaultValue="switcher">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="switcher" className="text-xs">
            Switcher
          </TabsTrigger>
          <TabsTrigger value="aux" className="text-xs">
            Aux
          </TabsTrigger>
          <TabsTrigger value="keyers" className="text-xs">
            Keyers
          </TabsTrigger>
          <TabsTrigger value="dsk" className="text-xs">
            DSK
          </TabsTrigger>
          <TabsTrigger value="audio" className="text-xs">
            Audio
          </TabsTrigger>
          <TabsTrigger value="media" className="text-xs">
            Media
          </TabsTrigger>
          <TabsTrigger value="supersource" className="text-xs">
            SSrc
          </TabsTrigger>
          <TabsTrigger value="color" className="text-xs">
            Color
          </TabsTrigger>
          <TabsTrigger value="multiview" className="text-xs">
            MV
          </TabsTrigger>
          <TabsTrigger value="macros" className="text-xs">
            Macros
          </TabsTrigger>
          <TabsTrigger value="inputs" className="text-xs">
            Inputs
          </TabsTrigger>
        </TabsList>

        {/* ─── Switcher Tab ─── */}
        <TabsContent value="switcher" className="mt-3 space-y-3">
          {/* M/E Selector */}
          {state.mixEffects.length > 1 && (
            <div className="flex gap-1">
              {state.mixEffects.map((_, i) => (
                <Button
                  key={i}
                  variant={selectedME === i ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setSelectedME(i)}
                >
                  M/E {i + 1}
                </Button>
              ))}
            </div>
          )}

          {state.mixEffects[selectedME] && (
            <SwitcherPanel
              me={state.mixEffects[selectedME]}
              meIndex={selectedME}
              inputs={allSources}
              upstreamKeyers={
                state.upstreamKeyers?.filter(
                  (_, i) => Math.floor(i / 4) === selectedME,
                ) ?? []
              }
              onCommand={send}
              disabled={isLoading}
            />
          )}
        </TabsContent>

        {/* ─── Aux Tab ─── */}
        <TabsContent value="aux" className="mt-3">
          <AuxPanel
            auxiliaries={state.auxiliaries}
            inputs={allSources}
            audioRouting={state.audio.audioRouting ?? []}
            fairlightInputs={state.audio.fairlightInputs ?? []}
            onCommand={send}
            disabled={isLoading}
          />
        </TabsContent>

        {/* ─── Upstream Keyers Tab ─── */}
        <TabsContent value="keyers" className="mt-3">
          <UpstreamKeyerPanel
            keyers={state.upstreamKeyers ?? []}
            meCount={state.mixEffects.length}
            inputs={allSources}
            onCommand={send}
            disabled={isLoading}
          />
        </TabsContent>

        {/* ─── DSK Tab ─── */}
        <TabsContent value="dsk" className="mt-3">
          <DownstreamKeyerPanel
            keyers={state.downstreamKeyers ?? []}
            inputs={allSources}
            onCommand={send}
            disabled={isLoading}
          />
        </TabsContent>

        {/* ─── Audio Tab ─── */}
        <TabsContent value="audio" className="mt-3">
          {state.audio ? (
            <AudioPanel
              audio={state.audio}
              inputs={allSources}
              onCommand={send}
              disabled={isLoading}
            />
          ) : (
            <EmptyState message="No audio data available" />
          )}
        </TabsContent>

        {/* ─── Media Tab ─── */}
        <TabsContent value="media" className="mt-3">
          <MediaPanel
            mediaPlayers={state.mediaPlayers ?? []}
            mediaPool={state.mediaPool}
            onCommand={send}
            disabled={isLoading}
          />
        </TabsContent>

        {/* ─── SuperSource Tab ─── */}
        <TabsContent value="supersource" className="mt-3">
          <SuperSourcePanel
            superSources={state.superSources ?? []}
            inputs={allSources}
            onCommand={send}
            disabled={isLoading}
          />
        </TabsContent>

        {/* ─── Color Generators Tab ─── */}
        <TabsContent value="color" className="mt-3">
          <ColorGenPanel
            colorGenerators={state.colorGenerators ?? []}
            onCommand={send}
            disabled={isLoading}
          />
        </TabsContent>

        {/* ─── MultiView Tab ─── */}
        <TabsContent value="multiview" className="mt-3">
          <MultiViewPanel
            multiViews={state.multiViews ?? []}
            inputs={allSources}
            onCommand={send}
            disabled={isLoading}
          />
        </TabsContent>

        {/* ─── Macros Tab ─── */}
        <TabsContent value="macros" className="mt-3">
          <MacroPanel
            macros={state.macros ?? []}
            onCommand={send}
            disabled={isLoading}
          />
        </TabsContent>

        {/* ─── Input Labels Tab ─── */}
        <TabsContent value="inputs" className="mt-3">
          <InputLabelsPanel
            inputs={state.inputs}
            onCommand={send}
            disabled={isLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Empty State                                                  */
/* ─────────────────────────────────────────────────────────── */

const EmptyState = ({ message }: { message: string }) => (
  <Card>
    <CardContent className="py-8 text-center text-sm text-muted-foreground">
      {message}
    </CardContent>
  </Card>
);

/* ─────────────────────────────────────────────────────────── */
/* Switcher Panel — Program/Preview bus + Transition controls  */
/* ─────────────────────────────────────────────────────────── */

interface SwitcherPanelProps {
  me: AtemMixEffect;
  meIndex: number;
  inputs: AtemInput[];
  upstreamKeyers: AtemUpstreamKeyer[];
  onCommand: (cmd: AtemCommand) => void;
  disabled?: boolean;
}

const TRANSITION_STYLES: { value: AtemTransitionStyle; label: string }[] = [
  { value: "mix", label: "MIX" },
  { value: "dip", label: "DIP" },
  { value: "wipe", label: "WIPE" },
  { value: "dve", label: "DVE" },
  { value: "sting", label: "STING" },
];

const SwitcherPanel = ({
  me,
  meIndex,
  inputs,
  upstreamKeyers,
  onCommand,
  disabled,
}: SwitcherPanelProps) => {
  // Show SDI inputs + key special sources for the bus
  const busInputs = inputs.filter((i) => i.index < 4000);

  return (
    <div className="space-y-3">
      {/* Program Bus */}
      <Card>
        <CardHeader className="pb-1 pt-3 px-3">
          <CardTitle className="text-xs font-medium text-red-500 uppercase tracking-wide">
            Program
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <SourceBus
            inputs={busInputs}
            activeIndex={me.programInput}
            onSelect={(idx) =>
              onCommand({ action: "set_program", meIndex, inputIndex: idx })
            }
            variant="program"
            disabled={disabled}
          />
        </CardContent>
      </Card>

      {/* Preview Bus */}
      <Card>
        <CardHeader className="pb-1 pt-3 px-3">
          <CardTitle className="text-xs font-medium text-green-500 uppercase tracking-wide">
            Preview
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <SourceBus
            inputs={busInputs}
            activeIndex={me.previewInput}
            onSelect={(idx) =>
              onCommand({ action: "set_preview", meIndex, inputIndex: idx })
            }
            variant="preview"
            disabled={disabled}
          />
        </CardContent>
      </Card>

      {/* Transition Controls + USK on-air + FTB */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Transition Style & Rate */}
        <Card>
          <CardHeader className="pb-1 pt-3 px-3">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Transition
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 space-y-2">
            <div className="flex flex-wrap gap-1">
              {TRANSITION_STYLES.map((ts) => (
                <Button
                  key={ts.value}
                  variant={
                    me.transitionSettings?.style === ts.value
                      ? "default"
                      : "outline"
                  }
                  size="sm"
                  className="h-7 text-[10px] px-2"
                  onClick={() =>
                    onCommand({
                      action: "set_transition_style",
                      meIndex,
                      style: ts.value,
                    })
                  }
                  disabled={disabled}
                >
                  {ts.label}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground w-10">Rate</Label>
              <Input
                type="number"
                min={1}
                max={250}
                value={me.transitionSettings?.rate ?? 30}
                onChange={(e) =>
                  onCommand({
                    action: "set_transition_rate",
                    meIndex,
                    rate: Number(e.target.value),
                  })
                }
                className="h-7 w-16 text-xs"
                disabled={disabled}
              />
              <span className="text-[10px] text-muted-foreground">frames</span>
            </div>
          </CardContent>
        </Card>

        {/* CUT / AUTO */}
        <Card>
          <CardHeader className="pb-1 pt-3 px-3">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Control
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                className="h-10 text-sm font-bold border-2"
                onClick={() => onCommand({ action: "cut", meIndex })}
                disabled={disabled}
              >
                CUT
              </Button>
              <Button
                variant={me.inTransition ? "destructive" : "default"}
                className="h-10 text-sm font-bold"
                onClick={() => onCommand({ action: "auto", meIndex })}
                disabled={disabled}
              >
                AUTO
              </Button>
            </div>
            {/* On-air keyers */}
            {upstreamKeyers.length > 0 && (
              <div className="flex gap-1 flex-wrap">
                {upstreamKeyers.map((usk, i) => (
                  <Button
                    key={usk.index}
                    variant={usk.onAir ? "destructive" : "outline"}
                    size="sm"
                    className="h-6 text-[10px] px-2"
                    onClick={() =>
                      onCommand({
                        action: "set_usk_on_air",
                        meIndex,
                        keyerIndex: usk.index,
                        onAir: !usk.onAir,
                      })
                    }
                    disabled={disabled}
                  >
                    KEY {i + 1}
                  </Button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Fade to Black */}
        <Card>
          <CardHeader className="pb-1 pt-3 px-3">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Fade to Black
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 space-y-2">
            <Button
              variant={me.fadeToBlack ? "destructive" : "outline"}
              className={cn(
                "h-10 w-full text-sm font-bold",
                me.fadeToBlack && "animate-pulse",
              )}
              onClick={() => onCommand({ action: "fade_to_black", meIndex })}
              disabled={disabled}
            >
              FTB
            </Button>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground w-10">Rate</Label>
              <Input
                type="number"
                min={1}
                max={250}
                value={me.fadeToBlackRate ?? 30}
                onChange={(e) =>
                  onCommand({
                    action: "set_ftb_rate",
                    meIndex,
                    rate: Number(e.target.value),
                  })
                }
                className="h-7 w-16 text-xs"
                disabled={disabled}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Source Bus — Row of source buttons (Program or Preview)     */
/* ─────────────────────────────────────────────────────────── */

const BUTTONS_PER_GROUP = 10;

interface SourceBusProps {
  inputs: AtemInput[];
  activeIndex: number;
  onSelect: (index: number) => void;
  variant: "program" | "preview";
  disabled?: boolean;
}

const SourceBus = ({
  inputs,
  activeIndex,
  onSelect,
  variant,
  disabled,
}: SourceBusProps) => {
  const [expandedGroup, setExpandedGroup] = useState(0);
  const needsGrouping = inputs.length > BUTTONS_PER_GROUP;

  const activeColor =
    variant === "program"
      ? "bg-red-600 text-white border-red-600"
      : "bg-green-600 text-white border-green-600";

  if (needsGrouping) {
    const groups: { label: string; items: AtemInput[] }[] = [];
    for (let i = 0; i < inputs.length; i += BUTTONS_PER_GROUP) {
      const chunk = inputs.slice(i, i + BUTTONS_PER_GROUP);
      groups.push({
        label: `${i + 1}–${Math.min(i + BUTTONS_PER_GROUP, inputs.length)}`,
        items: chunk,
      });
    }

    return (
      <div className="space-y-2">
        <div className="flex gap-1 flex-wrap">
          {groups.map((g, gi) => (
            <button
              key={gi}
              className={cn(
                "text-[10px] font-medium px-1.5 py-0.5 rounded transition-colors",
                expandedGroup === gi
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
              onClick={() => setExpandedGroup(gi)}
            >
              {g.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-5 sm:grid-cols-10 gap-1">
          {groups[expandedGroup]?.items.map((input) => (
            <button
              key={input.index}
              className={cn(
                "flex items-center justify-center rounded border text-center transition-colors h-8 text-[10px] font-medium",
                activeIndex === input.index
                  ? activeColor
                  : "bg-card hover:bg-accent border-border",
                disabled && "opacity-50 cursor-not-allowed",
              )}
              onClick={() => onSelect(input.index)}
              disabled={disabled}
              title={input.longName}
            >
              {input.shortName}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-5 sm:grid-cols-10 gap-1">
      {inputs.map((input) => (
        <button
          key={input.index}
          className={cn(
            "flex items-center justify-center rounded border text-center transition-colors h-8 text-[10px] font-medium",
            activeIndex === input.index
              ? activeColor
              : "bg-card hover:bg-accent border-border",
            disabled && "opacity-50 cursor-not-allowed",
          )}
          onClick={() => onSelect(input.index)}
          disabled={disabled}
          title={input.longName}
        >
          {input.shortName}
        </button>
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Source Combobox — searchable input picker                    */
/* ─────────────────────────────────────────────────────────── */

interface SourceComboboxProps {
  inputs: AtemInput[];
  value: number;
  onSelect: (index: number) => void;
  disabled?: boolean;
  className?: string;
}

const SourceCombobox = ({
  inputs,
  value,
  onSelect,
  disabled,
  className,
}: SourceComboboxProps) => {
  const [open, setOpen] = useState(false);
  const current = inputs.find((i) => i.index === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-7 justify-between text-xs font-normal px-2",
            className,
          )}
          disabled={disabled}
        >
          <span className="truncate">
            {current?.shortName ?? `Src ${value}`}
          </span>
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search..." className="h-8 text-xs" />
          <CommandList className="max-h-[200px]">
            <CommandEmpty className="py-3 text-xs">
              No source found.
            </CommandEmpty>
            <CommandGroup>
              {inputs.map((input) => (
                <CommandItem
                  key={input.index}
                  value={`${input.shortName} ${input.longName}`}
                  onSelect={() => {
                    onSelect(input.index);
                    setOpen(false);
                  }}
                  className="text-xs"
                >
                  <Check
                    className={cn(
                      "mr-1.5 h-3 w-3",
                      value === input.index ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="font-mono mr-1.5">{input.shortName}</span>
                  <span className="text-muted-foreground truncate">
                    {input.longName}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Audio source helpers                                        */
/* ─────────────────────────────────────────────────────────── */

/** Resolve the display name for a Fairlight input. SDI inputs derive from video label. */
const resolveAudioInputName = (
  fi: AtemFairlightInput,
  videoInputs: AtemInput[],
): string => {
  if (fi.externalPortType === "sdi") {
    const vi = videoInputs.find((i) => i.index === fi.index);
    return vi?.longName ?? fi.longName ?? `In ${fi.index + 1}`;
  }
  return fi.longName || `In ${fi.index}`;
};

/** Build a flat list of audio source pairs for routing dropdowns. */
const buildAudioSourcePairs = (
  fairlightInputs: AtemFairlightInput[],
  videoInputs: AtemInput[],
): { inputIndex: number; sourceId: number; label: string }[] => {
  const pairs: { inputIndex: number; sourceId: number; label: string }[] = [
    { inputIndex: -1, sourceId: -1, label: "Program Mix" },
  ];
  for (const fi of fairlightInputs) {
    const inputName = resolveAudioInputName(fi, videoInputs);
    if (fi.sources.length === 1) {
      pairs.push({ inputIndex: fi.index, sourceId: 0, label: inputName });
    } else {
      for (const src of fi.sources) {
        pairs.push({
          inputIndex: fi.index,
          sourceId: src.sourceId,
          label: `${inputName} Ch${src.sourceId * 2 + 1}/${src.sourceId * 2 + 2}`,
        });
      }
    }
  }
  return pairs;
};

/* ─────────────────────────────────────────────────────────── */
/* Audio Source Combobox — searchable dropdown for audio pairs */
/* ─────────────────────────────────────────────────────────── */

interface AudioSourceComboboxProps {
  sources: { inputIndex: number; sourceId: number; label: string }[];
  value: string; // "inputIndex:sourceId"
  onSelect: (inputIndex: number, sourceId: number) => void;
  disabled?: boolean;
  className?: string;
}

const AudioSourceCombobox = ({
  sources,
  value,
  onSelect,
  disabled,
  className,
}: AudioSourceComboboxProps) => {
  const [open, setOpen] = useState(false);
  const current = sources.find(
    (s) => `${s.inputIndex}:${s.sourceId}` === value,
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-5 justify-between text-[9px] font-normal px-1.5",
            className,
          )}
          disabled={disabled}
        >
          <span className="truncate">{current?.label ?? "Program Mix"}</span>
          <ChevronsUpDown className="h-2.5 w-2.5 shrink-0 opacity-50 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search..." className="h-7 text-xs" />
          <CommandList className="max-h-[200px]">
            <CommandEmpty className="py-3 text-xs">
              No source found.
            </CommandEmpty>
            <CommandGroup>
              {sources.map((sp) => {
                const key = `${sp.inputIndex}:${sp.sourceId}`;
                return (
                  <CommandItem
                    key={key}
                    value={sp.label}
                    onSelect={() => {
                      onSelect(sp.inputIndex, sp.sourceId);
                      setOpen(false);
                    }}
                    className="text-xs"
                  >
                    <Check
                      className={cn(
                        "mr-1.5 h-3 w-3",
                        value === key ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {sp.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Aux Panel — Auxiliary output routing                         */
/* ─────────────────────────────────────────────────────────── */

interface AuxPanelProps {
  auxiliaries: AtemAuxOutput[];
  inputs: AtemInput[];
  audioRouting: AtemAudioRoutingOutput[];
  fairlightInputs: AtemFairlightInput[];
  onCommand: (cmd: AtemCommand) => void;
  disabled?: boolean;
}

const AuxPanel = ({
  auxiliaries,
  inputs,
  audioRouting,
  fairlightInputs,
  onCommand,
  disabled,
}: AuxPanelProps) => {
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editShort, setEditShort] = useState("");
  const [editLong, setEditLong] = useState("");
  const [expandedAux, setExpandedAux] = useState<Set<number>>(new Set());

  const startEdit = (aux: AtemAuxOutput) => {
    setEditingIdx(aux.index);
    setEditShort(aux.shortName);
    setEditLong(aux.longName);
  };

  const saveEdit = (auxIndex: number) => {
    onCommand({
      action: "label_aux",
      auxIndex,
      shortName: editShort,
      longName: editLong,
    });
    setEditingIdx(null);
  };

  const toggleExpanded = (idx: number) => {
    setExpandedAux((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // Build audio source pairs for dropdowns (same logic as AudioRoutingSection)
  const allSourcePairs = useMemo(
    () => buildAudioSourcePairs(fairlightInputs, inputs),
    [fairlightInputs, inputs],
  );

  // Find the audio routing output for a given aux index (aux routing starts at index 2)
  const getAuxRouting = (auxIndex: number) =>
    audioRouting.find(
      (r) => r.sourceType === "aux" && r.index === auxIndex + 2,
    );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Auxiliary Outputs</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="divide-y">
          {auxiliaries.map((aux) => {
            const routing = getAuxRouting(aux.index);
            // All 6 audio channel pairs (SDI embedded audio)
            const audioPairs = routing?.channelPairs ?? [];
            const isExpanded = expandedAux.has(aux.index);

            return (
              <div key={aux.index} className="py-1.5">
                <div className="flex items-center gap-2">
                  {editingIdx === aux.index ? (
                    <>
                      <Input
                        value={editShort}
                        onChange={(e) =>
                          setEditShort(e.target.value.slice(0, 8))
                        }
                        className="h-6 w-16 text-[10px] font-mono"
                        placeholder="Short"
                      />
                      <Input
                        value={editLong}
                        onChange={(e) =>
                          setEditLong(e.target.value.slice(0, 24))
                        }
                        className="h-6 flex-1 text-[10px]"
                        placeholder="Long name"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0"
                        onClick={() => saveEdit(aux.index)}
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0"
                        onClick={() => setEditingIdx(null)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <button
                        className="text-xs font-medium min-w-[60px] text-left hover:underline"
                        onClick={() => startEdit(aux)}
                        title={`${aux.longName} — click to rename`}
                      >
                        {aux.shortName}
                      </button>
                      <SourceCombobox
                        inputs={inputs}
                        value={aux.source}
                        onSelect={(inputIndex) =>
                          onCommand({
                            action: "set_aux",
                            auxIndex: aux.index,
                            inputIndex,
                          })
                        }
                        disabled={disabled}
                        className="w-[160px]"
                      />
                      {audioPairs.length > 0 && (
                        <button
                          className="text-[9px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-0.5"
                          onClick={() => toggleExpanded(aux.index)}
                          title="Audio pairs 1-6 (SDI embedded)"
                        >
                          <Volume2 className="h-3 w-3" />
                          {isExpanded ? (
                            <ChevronDown className="h-3 w-3" />
                          ) : (
                            <ChevronRight className="h-3 w-3" />
                          )}
                        </button>
                      )}
                    </>
                  )}
                </div>

                {/* Audio pairs 1-6 — SDI embedded audio routing */}
                {isExpanded && routing && audioPairs.length > 0 && (
                  <div className="ml-16 mt-1 space-y-0.5 border-l-2 border-muted pl-2">
                    <span className="text-[9px] text-muted-foreground">
                      Audio pairs 1–6 (SDI embedded)
                    </span>
                    {audioPairs.map((pair) => {
                      return (
                        <div
                          key={pair.pairIndex}
                          className="flex items-center gap-1.5"
                        >
                          <span className="text-[9px] font-mono w-4 text-muted-foreground shrink-0">
                            {pair.pairIndex + 1}
                          </span>
                          <Switch
                            checked={pair.enabled}
                            onCheckedChange={(enabled) =>
                              onCommand({
                                action: "set_audio_routing_channel_pair",
                                outputIndex: routing.index,
                                pairIndex: pair.pairIndex,
                                sourceInputIndex: pair.sourceInputIndex,
                                sourceId: pair.sourceId,
                                enabled,
                              })
                            }
                            disabled={disabled}
                          />
                          <AudioSourceCombobox
                            sources={allSourcePairs}
                            value={`${pair.sourceInputIndex}:${pair.sourceId}`}
                            onSelect={(inIdx, srcId) =>
                              onCommand({
                                action: "set_audio_routing_channel_pair",
                                outputIndex: routing.index,
                                pairIndex: pair.pairIndex,
                                sourceInputIndex: inIdx,
                                sourceId: srcId,
                                enabled: pair.enabled,
                              })
                            }
                            disabled={disabled}
                            className="flex-1 min-w-0"
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Upstream Keyer Panel                                         */
/* ─────────────────────────────────────────────────────────── */

interface UpstreamKeyerPanelProps {
  keyers: AtemUpstreamKeyer[];
  meCount: number;
  inputs: AtemInput[];
  onCommand: (cmd: AtemCommand) => void;
  disabled?: boolean;
}

const KEY_TYPES: { value: AtemUpstreamKeyer["type"]; label: string }[] = [
  { value: "luma", label: "Luma" },
  { value: "chroma", label: "Chroma" },
  { value: "pattern", label: "Pattern" },
  { value: "dve", label: "DVE" },
];

const UpstreamKeyerPanel = ({
  keyers,
  meCount,
  inputs,
  onCommand,
  disabled,
}: UpstreamKeyerPanelProps) => {
  const [selectedME, setSelectedME] = useState(0);
  const keyersPerME = meCount > 0 ? Math.floor(keyers.length / meCount) : 0;
  const meKeyers = keyers.slice(
    selectedME * keyersPerME,
    (selectedME + 1) * keyersPerME,
  );

  if (keyers.length === 0)
    return <EmptyState message="No upstream keyers available" />;

  return (
    <div className="space-y-3">
      {meCount > 1 && (
        <div className="flex gap-1">
          {Array.from({ length: meCount }, (_, i) => (
            <Button
              key={i}
              variant={selectedME === i ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setSelectedME(i)}
            >
              M/E {i + 1}
            </Button>
          ))}
        </div>
      )}

      {meKeyers.map((usk, localIdx) => (
        <Card key={usk.index}>
          <CardHeader className="pb-2 pt-3 px-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">USK {localIdx + 1}</CardTitle>
              <Button
                variant={usk.onAir ? "destructive" : "outline"}
                size="sm"
                className="h-6 text-[10px]"
                onClick={() =>
                  onCommand({
                    action: "set_usk_on_air",
                    meIndex: selectedME,
                    keyerIndex: usk.index,
                    onAir: !usk.onAir,
                  })
                }
                disabled={disabled}
              >
                {usk.onAir ? "ON AIR" : "OFF"}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-3 space-y-2">
            {/* Key Type */}
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground w-12">Type</Label>
              <div className="flex gap-1">
                {KEY_TYPES.map((kt) => (
                  <Button
                    key={kt.value}
                    variant={usk.type === kt.value ? "default" : "outline"}
                    size="sm"
                    className="h-6 text-[10px] px-2"
                    onClick={() =>
                      onCommand({
                        action: "set_usk_type",
                        meIndex: selectedME,
                        keyerIndex: usk.index,
                        type: kt.value,
                      })
                    }
                    disabled={disabled}
                  >
                    {kt.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Fill & Key Sources */}
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground w-8">
                  Fill
                </Label>
                <SourceCombobox
                  inputs={inputs}
                  value={usk.fillSource}
                  onSelect={(source) =>
                    onCommand({
                      action: "set_usk_fill_source",
                      meIndex: selectedME,
                      keyerIndex: usk.index,
                      source,
                    })
                  }
                  disabled={disabled}
                  className="flex-1"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground w-8">Key</Label>
                <SourceCombobox
                  inputs={inputs}
                  value={usk.keySource}
                  onSelect={(source) =>
                    onCommand({
                      action: "set_usk_key_source",
                      meIndex: selectedME,
                      keyerIndex: usk.index,
                      source,
                    })
                  }
                  disabled={disabled}
                  className="flex-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Downstream Keyer Panel                                       */
/* ─────────────────────────────────────────────────────────── */

interface DownstreamKeyerPanelProps {
  keyers: AtemDownstreamKeyer[];
  inputs: AtemInput[];
  onCommand: (cmd: AtemCommand) => void;
  disabled?: boolean;
}

const DownstreamKeyerPanel = ({
  keyers,
  inputs,
  onCommand,
  disabled,
}: DownstreamKeyerPanelProps) => {
  if (keyers.length === 0)
    return <EmptyState message="No downstream keyers available" />;

  return (
    <div className="space-y-3">
      {keyers.map((dsk) => (
        <Card key={dsk.index}>
          <CardHeader className="pb-2 pt-3 px-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">DSK {dsk.index + 1}</CardTitle>
              <div className="flex gap-1">
                <Button
                  variant={dsk.tie ? "secondary" : "outline"}
                  size="sm"
                  className="h-6 text-[10px]"
                  onClick={() =>
                    onCommand({
                      action: "set_dsk_tie",
                      dskIndex: dsk.index,
                      tie: !dsk.tie,
                    })
                  }
                  disabled={disabled}
                >
                  TIE
                </Button>
                <Button
                  variant={dsk.onAir ? "destructive" : "outline"}
                  size="sm"
                  className="h-6 text-[10px]"
                  onClick={() =>
                    onCommand({
                      action: "set_dsk_on_air",
                      dskIndex: dsk.index,
                      onAir: !dsk.onAir,
                    })
                  }
                  disabled={disabled}
                >
                  {dsk.onAir ? "ON AIR" : "OFF"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 text-[10px]"
                  onClick={() =>
                    onCommand({ action: "set_dsk_auto", dskIndex: dsk.index })
                  }
                  disabled={disabled}
                >
                  AUTO
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-3 space-y-2">
            {/* Fill & Key Sources */}
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground w-8">
                  Fill
                </Label>
                <SourceCombobox
                  inputs={inputs}
                  value={dsk.fillSource}
                  onSelect={(source) =>
                    onCommand({
                      action: "set_dsk_fill_source",
                      dskIndex: dsk.index,
                      source,
                    })
                  }
                  disabled={disabled}
                  className="flex-1"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground w-8">Key</Label>
                <SourceCombobox
                  inputs={inputs}
                  value={dsk.keySource}
                  onSelect={(source) =>
                    onCommand({
                      action: "set_dsk_key_source",
                      dskIndex: dsk.index,
                      source,
                    })
                  }
                  disabled={disabled}
                  className="flex-1"
                />
              </div>
            </div>

            {/* Rate */}
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground w-12">Rate</Label>
              <Input
                type="number"
                min={1}
                max={250}
                value={dsk.rate}
                onChange={(e) =>
                  onCommand({
                    action: "set_dsk_rate",
                    dskIndex: dsk.index,
                    rate: Number(e.target.value),
                  })
                }
                className="h-7 w-16 text-xs"
                disabled={disabled}
              />
              <span className="text-[10px] text-muted-foreground">frames</span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Audio Panel                                                  */
/* ─────────────────────────────────────────────────────────── */

interface AudioPanelProps {
  audio: AtemAudioState;
  inputs: AtemInput[];
  onCommand: (cmd: AtemCommand) => void;
  disabled?: boolean;
}

type AtemAudioState = NonNullable<AtemState["audio"]>;

const AudioPanel = ({
  audio,
  inputs,
  onCommand,
  disabled,
}: AudioPanelProps) => {
  const [audioTab, setAudioTab] = useState<
    "mixer" | "monitor" | "routing" | "labels"
  >("mixer");

  return (
    <div className="space-y-3">
      {/* Sub-tabs */}
      <div className="flex gap-1 border-b pb-1">
        {(["mixer", "monitor", "routing", "labels"] as const).map((tab) => (
          <button
            key={tab}
            className={cn(
              "text-xs font-medium px-2 py-1 rounded-t transition-colors capitalize",
              audioTab === tab
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
            onClick={() => setAudioTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {audioTab === "mixer" && (
        <AudioMixerSection
          audio={audio}
          inputs={inputs}
          onCommand={onCommand}
          disabled={disabled}
        />
      )}
      {audioTab === "monitor" && (
        <AudioMonitorSection
          audio={audio}
          onCommand={onCommand}
          disabled={disabled}
        />
      )}
      {audioTab === "routing" && (
        <AudioRoutingSection
          audio={audio}
          inputs={inputs}
          onCommand={onCommand}
          disabled={disabled}
        />
      )}
      {audioTab === "labels" && (
        <AudioLabelsSection
          audio={audio}
          inputs={inputs}
          onCommand={onCommand}
          disabled={disabled}
        />
      )}
    </div>
  );
};

/* Port type labels */
const PORT_TYPE_LABELS: Record<string, string> = {
  sdi: "SDI",
  hdmi: "HDMI",
  component: "Comp",
  composite: "Comp",
  xlr: "XLR",
  aes_ebu: "AES",
  rca: "RCA",
  madi: "MADI",
  trs: "TRS",
  internal: "Int",
};

/* Audio Mixer sub-section — Fairlight source-aware with MADI support */
const AudioMixerSection = ({
  audio,
  inputs,
  onCommand,
  disabled,
}: AudioPanelProps) => {
  const [showAll, setShowAll] = useState(false);
  const [expandedInputs, setExpandedInputs] = useState<Set<number>>(new Set());
  const [viewMode, setViewMode] = useState<"classic" | "fairlight">(
    "fairlight",
  );
  const [renamingInput, setRenamingInput] = useState<number | null>(null);
  const [renameShort, setRenameShort] = useState("");
  const [renameLong, setRenameLong] = useState("");

  const startRenameInput = (fi: AtemFairlightInput) => {
    setRenamingInput(fi.index);
    setRenameShort(fi.shortName);
    setRenameLong(fi.longName);
  };

  const saveRenameInput = (inputIndex: number) => {
    onCommand({
      action: "label_fairlight_input",
      inputIndex,
      shortName: renameShort,
      longName: renameLong,
    });
    setRenamingInput(null);
  };
  const fairlightInputs = useMemo(
    () => audio.fairlightInputs ?? [],
    [audio.fairlightInputs],
  );
  const hasFairlight = fairlightInputs.length > 0;

  // Count active sources across all inputs
  const activeFairlightCount = fairlightInputs.reduce(
    (acc, fi) => acc + fi.sources.filter((s) => s.mixOption !== "off").length,
    0,
  );
  const totalFairlightSources = fairlightInputs.reduce(
    (acc, fi) => acc + fi.sources.length,
    0,
  );

  // Filter displayed inputs
  const displayFairlightInputs = showAll
    ? fairlightInputs
    : fairlightInputs.filter((fi) =>
        fi.sources.some((s) => s.mixOption !== "off"),
      );

  // Classic channels
  const activeChannels = audio.channels.filter((ch) => ch.mixOption !== "off");
  const displayChannels = showAll ? audio.channels : activeChannels;

  const getChannelName = (ch: AtemAudioChannel) => {
    const input = inputs.find((i) => i.index === ch.index);
    return input?.shortName ?? `Ch ${ch.index + 1}`;
  };

  const getInputName = (fi: AtemFairlightInput) =>
    resolveAudioInputName(fi, inputs);

  const toggleExpanded = (idx: number) => {
    setExpandedInputs((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <>
      {/* Master */}
      <Card>
        <CardHeader className="pb-2 pt-3 px-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Master Output</CardTitle>
            <div className="flex items-center gap-2">
              <Switch
                checked={audio.master.followFadeToBlack}
                disabled={disabled}
              />
              <Label className="text-xs">AFV</Label>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <div className="flex items-center gap-3">
            <Volume2 className="h-4 w-4 text-muted-foreground shrink-0" />
            <Slider
              value={[audio.master.gain]}
              min={-60}
              max={6}
              step={0.5}
              onValueChange={([val]) =>
                onCommand({ action: "set_audio_master_gain", gain: val })
              }
              disabled={disabled}
              className="flex-1"
            />
            <span className="text-xs font-mono w-12 text-right">
              {audio.master.gain.toFixed(1)} dB
            </span>
          </div>
          {/* VU meters */}
          <div className="flex gap-1 mt-2">
            <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all"
                style={{ width: `${(audio.master.leftLevel ?? 0) * 100}%` }}
              />
            </div>
            <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all"
                style={{ width: `${(audio.master.rightLevel ?? 0) * 100}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* View mode + filter toggle */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          {hasFairlight && (
            <div className="flex gap-0.5">
              {(["fairlight", "classic"] as const).map((mode) => (
                <button
                  key={mode}
                  className={cn(
                    "text-[10px] font-medium px-1.5 py-0.5 rounded border transition-colors capitalize",
                    viewMode === mode
                      ? "bg-accent text-accent-foreground border-accent"
                      : "border-border text-muted-foreground hover:bg-accent/50",
                  )}
                  onClick={() => setViewMode(mode)}
                >
                  {mode}
                </button>
              ))}
            </div>
          )}
          <span className="text-xs text-muted-foreground">
            {viewMode === "fairlight"
              ? `${activeFairlightCount} active / ${totalFairlightSources} sources`
              : `${activeChannels.length} active / ${audio.channels.length} total`}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-xs"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? "Show Active" : "Show All"}
        </Button>
      </div>

      {/* Fairlight view — inputs with expandable channel pair sources */}
      {viewMode === "fairlight" && hasFairlight && (
        <div className="space-y-1">
          {displayFairlightInputs.map((fi) => {
            const inputName = getInputName(fi);
            const portLabel =
              PORT_TYPE_LABELS[fi.externalPortType] ?? fi.externalPortType;
            const isMultiSource = fi.sources.length > 1;
            const isExpanded = expandedInputs.has(fi.index);
            const activeSourceCount = fi.sources.filter(
              (s) => s.mixOption !== "off",
            ).length;

            // Can this input be renamed? (only non-SDI — SDI gets name from video input label)
            const canRename = fi.externalPortType !== "sdi";

            // Single-source input: show inline
            if (!isMultiSource) {
              const src = fi.sources[0];
              if (!src) return null;
              if (canRename && renamingInput === fi.index) {
                return (
                  <div key={fi.index} className="flex items-center gap-1 py-1">
                    <Input
                      value={renameShort}
                      onChange={(e) =>
                        setRenameShort(e.target.value.slice(0, 8))
                      }
                      className="h-5 w-14 text-[9px] font-mono px-1"
                      placeholder="Short"
                      autoFocus
                    />
                    <Input
                      value={renameLong}
                      onChange={(e) =>
                        setRenameLong(e.target.value.slice(0, 24))
                      }
                      className="h-5 flex-1 text-[9px] px-1"
                      placeholder="Long name"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0"
                      onClick={() => saveRenameInput(fi.index)}
                    >
                      <Check className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0"
                      onClick={() => setRenamingInput(null)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                );
              }
              return (
                <FairlightSourceStrip
                  key={fi.index}
                  label={inputName}
                  portType={portLabel}
                  source={src}
                  inputIndex={fi.index}
                  onCommand={onCommand}
                  disabled={disabled}
                  onRename={canRename ? () => startRenameInput(fi) : undefined}
                  longName={fi.longName}
                />
              );
            }

            // Multi-source input: expandable group (rename only for non-SDI)
            if (canRename && renamingInput === fi.index) {
              return (
                <div
                  key={fi.index}
                  className="border rounded-md p-2 flex items-center gap-1"
                >
                  <Input
                    value={renameShort}
                    onChange={(e) => setRenameShort(e.target.value.slice(0, 8))}
                    className="h-5 w-14 text-[9px] font-mono px-1"
                    placeholder="Short"
                    autoFocus
                  />
                  <Input
                    value={renameLong}
                    onChange={(e) => setRenameLong(e.target.value.slice(0, 24))}
                    className="h-5 flex-1 text-[9px] px-1"
                    placeholder="Long name"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0"
                    onClick={() => saveRenameInput(fi.index)}
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0"
                    onClick={() => setRenamingInput(null)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              );
            }

            return (
              <div key={fi.index} className="border rounded-md">
                <div
                  className="flex items-center gap-2 w-full px-2 py-1.5 text-left hover:bg-accent/50 transition-colors cursor-pointer"
                  onClick={() => toggleExpanded(fi.index)}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-3 w-3 shrink-0" />
                  ) : (
                    <ChevronRight className="h-3 w-3 shrink-0" />
                  )}
                  {canRename ? (
                    <button
                      className="text-[10px] font-mono font-medium hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        startRenameInput(fi);
                      }}
                      title={`${fi.longName} — click to rename`}
                    >
                      {inputName}
                    </button>
                  ) : (
                    <span className="text-[10px] font-mono font-medium">
                      {inputName}
                    </span>
                  )}
                  <Badge variant="outline" className="text-[8px] h-4 px-1">
                    {portLabel}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground ml-auto">
                    {activeSourceCount}/{fi.sources.length} pairs active
                  </span>
                  {/* Configuration selector */}
                  {fi.supportedConfigurations &&
                    fi.supportedConfigurations.length > 1 && (
                      <Select
                        value={fi.activeConfiguration}
                        onValueChange={(val) =>
                          onCommand({
                            action: "set_fairlight_input_configuration",
                            inputIndex: fi.index,
                            configuration:
                              val as AtemFairlightInputConfiguration,
                          })
                        }
                      >
                        <SelectTrigger
                          className="h-5 w-16 text-[9px] px-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {fi.supportedConfigurations.map((cfg) => (
                            <SelectItem
                              key={cfg}
                              value={cfg}
                              className="text-[10px]"
                            >
                              {cfg}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                </div>
                {isExpanded && (
                  <div className="px-2 pb-2 space-y-0.5 border-t">
                    {fi.sources.map((src) => (
                      <FairlightSourceStrip
                        key={src.sourceId}
                        label={`Ch ${src.sourceId * 2 + 1}/${src.sourceId * 2 + 2}`}
                        portType={src.sourceType === "mono" ? "M" : "St"}
                        source={src}
                        inputIndex={fi.index}
                        onCommand={onCommand}
                        disabled={disabled}
                        compact
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Classic channel view */}
      {(viewMode === "classic" || !hasFairlight) && (
        <div className="space-y-1">
          {displayChannels.map((ch) => (
            <div key={ch.index} className="flex items-center gap-2 py-1">
              <span
                className="text-[10px] font-mono w-10 truncate"
                title={getChannelName(ch)}
              >
                {getChannelName(ch)}
              </span>
              {/* Mix option buttons */}
              <div className="flex gap-0.5">
                {(["off", "on", "afv"] as const).map((opt) => (
                  <button
                    key={opt}
                    className={cn(
                      "text-[9px] font-medium px-1 py-0.5 rounded border transition-colors",
                      ch.mixOption === opt
                        ? opt === "on"
                          ? "bg-red-600 text-white border-red-600"
                          : opt === "afv"
                            ? "bg-yellow-600 text-white border-yellow-600"
                            : "bg-muted text-muted-foreground border-border"
                        : "border-border text-muted-foreground hover:bg-accent",
                    )}
                    onClick={() =>
                      onCommand({
                        action: "set_audio_mix_option",
                        channelIndex: ch.index,
                        mixOption: opt,
                      })
                    }
                    disabled={disabled}
                  >
                    {opt.toUpperCase()}
                  </button>
                ))}
              </div>
              <Slider
                value={[ch.gain]}
                min={-60}
                max={6}
                step={0.5}
                onValueChange={([val]) =>
                  onCommand({
                    action: "set_audio_gain",
                    channelIndex: ch.index,
                    gain: val,
                  })
                }
                disabled={disabled}
                className="flex-1"
              />
              <span className="text-[10px] font-mono w-10 text-right">
                {ch.gain.toFixed(0)}dB
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

/* Fairlight source strip — a single channel pair within an input */
const FairlightSourceStrip = ({
  label,
  portType,
  source,
  inputIndex,
  onCommand,
  disabled,
  compact,
  onRename,
  longName,
}: {
  label: string;
  portType: string;
  source: AtemFairlightSource;
  inputIndex: number;
  onCommand: (cmd: AtemCommand) => void;
  disabled?: boolean;
  compact?: boolean;
  onRename?: () => void;
  longName?: string;
}) => (
  <div className={cn("flex items-center gap-2", compact ? "py-0.5" : "py-1")}>
    {onRename ? (
      <button
        className={cn(
          "font-mono truncate text-left hover:underline",
          compact ? "text-[9px] w-12" : "text-[10px] w-10",
        )}
        title={longName ? `${longName} — click to rename` : label}
        onClick={onRename}
      >
        {label}
      </button>
    ) : (
      <span
        className={cn(
          "font-mono truncate",
          compact ? "text-[9px] w-12" : "text-[10px] w-10",
        )}
        title={label}
      >
        {label}
      </span>
    )}
    {!compact && (
      <Badge variant="outline" className="text-[8px] h-4 px-1 shrink-0">
        {portType}
      </Badge>
    )}
    {/* Mix option buttons */}
    <div className="flex gap-0.5">
      {(["off", "on", "afv"] as const).map((opt) => (
        <button
          key={opt}
          className={cn(
            "text-[9px] font-medium px-1 py-0.5 rounded border transition-colors",
            source.mixOption === opt
              ? opt === "on"
                ? "bg-red-600 text-white border-red-600"
                : opt === "afv"
                  ? "bg-yellow-600 text-white border-yellow-600"
                  : "bg-muted text-muted-foreground border-border"
              : "border-border text-muted-foreground hover:bg-accent",
          )}
          onClick={() =>
            onCommand({
              action: "set_fairlight_input_mix_option",
              inputIndex,
              sourceId: source.sourceId,
              mixOption: opt,
            })
          }
          disabled={disabled}
        >
          {opt.toUpperCase()}
        </button>
      ))}
    </div>
    {/* Fader */}
    <Slider
      value={[source.faderGain]}
      min={-100}
      max={10}
      step={0.5}
      onValueChange={([val]) =>
        onCommand({
          action: "set_fairlight_input_fader",
          inputIndex,
          sourceId: source.sourceId,
          faderGain: val,
        })
      }
      disabled={disabled}
      className="flex-1"
    />
    <span className="text-[10px] font-mono w-10 text-right">
      {source.faderGain.toFixed(0)}dB
    </span>
  </div>
);

/* Audio Monitor sub-section */
const AudioMonitorSection = ({
  audio,
  onCommand,
  disabled,
}: {
  audio: AtemAudioState;
  onCommand: (cmd: AtemCommand) => void;
  disabled?: boolean;
}) => (
  <div className="space-y-3">
    {/* Monitor Output */}
    <Card>
      <CardHeader className="pb-2 pt-3 px-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Monitor Output</CardTitle>
          <Button
            variant={audio.monitorMute ? "destructive" : "outline"}
            size="sm"
            className="h-6 text-[10px]"
            onClick={() =>
              onCommand({
                action: "set_audio_monitor_mute",
                mute: !audio.monitorMute,
              })
            }
            disabled={disabled}
          >
            {audio.monitorMute ? "MUTED" : "MUTE"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-3 pb-3">
        <div className="flex items-center gap-3">
          <Volume2 className="h-4 w-4 text-muted-foreground shrink-0" />
          <Slider
            value={[audio.monitorGain]}
            min={-60}
            max={6}
            step={0.5}
            onValueChange={([val]) =>
              onCommand({ action: "set_audio_monitor_gain", gain: val })
            }
            disabled={disabled}
            className="flex-1"
          />
          <span className="text-xs font-mono w-12 text-right">
            {audio.monitorGain.toFixed(1)} dB
          </span>
        </div>
      </CardContent>
    </Card>

    {/* Solo */}
    <Card>
      <CardHeader className="pb-2 pt-3 px-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Solo</CardTitle>
          <Switch
            checked={audio.monitorSolo ?? false}
            onCheckedChange={(solo) =>
              onCommand({
                action: "set_audio_monitor_solo",
                solo,
                inputIndex: audio.monitorSoloInput ?? -1,
              })
            }
            disabled={disabled}
          />
        </div>
      </CardHeader>
      <CardContent className="px-3 pb-3">
        <p className="text-xs text-muted-foreground">
          {audio.monitorSolo
            ? `Soloing input ${(audio.monitorSoloInput ?? -1) + 1}`
            : "Solo disabled — hearing full program mix"}
        </p>
      </CardContent>
    </Card>

    {/* Headphone Output */}
    <Card>
      <CardHeader className="pb-2 pt-3 px-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Headphone</CardTitle>
          <Button
            variant={audio.headphoneMute ? "destructive" : "outline"}
            size="sm"
            className="h-6 text-[10px]"
            onClick={() =>
              onCommand({
                action: "set_audio_headphone_mute",
                mute: !(audio.headphoneMute ?? false),
              })
            }
            disabled={disabled}
          >
            {audio.headphoneMute ? "MUTED" : "MUTE"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-3 pb-3">
        <div className="flex items-center gap-3">
          <Volume2 className="h-4 w-4 text-muted-foreground shrink-0" />
          <Slider
            value={[audio.headphoneGain ?? 0]}
            min={-60}
            max={6}
            step={0.5}
            onValueChange={([val]) =>
              onCommand({ action: "set_audio_headphone_gain", gain: val })
            }
            disabled={disabled}
            className="flex-1"
          />
          <span className="text-xs font-mono w-12 text-right">
            {(audio.headphoneGain ?? 0).toFixed(1)} dB
          </span>
        </div>
      </CardContent>
    </Card>
  </div>
);

/* Audio Labels sub-section — bulk naming for MADI channel pairs */
const AudioLabelsSection = ({
  audio,
  onCommand,
  disabled,
}: AudioPanelProps) => {
  const fairlightInputs = useMemo(
    () => audio.fairlightInputs ?? [],
    [audio.fairlightInputs],
  );
  const madi1 = fairlightInputs.filter(
    (fi) =>
      fi.externalPortType === "madi" && fi.index >= 3001 && fi.index < 3100,
  );
  const madi2 = fairlightInputs.filter(
    (fi) =>
      fi.externalPortType === "madi" && fi.index >= 3101 && fi.index < 3200,
  );

  const renderGroup = (label: string, inputs: AtemFairlightInput[]) => (
    <Card>
      <CardHeader className="pb-2 pt-3 px-3">
        <CardTitle className="text-sm">
          {label} ({inputs.length} pairs)
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-3">
        {inputs.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">
            No MADI inputs configured.
          </p>
        ) : (
          <ScrollArea className={cn(inputs.length > 12 && "h-80")}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead className="w-24">Short Name</TableHead>
                  <TableHead>Long Name</TableHead>
                  <TableHead className="w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inputs.map((fi) => (
                  <AtemLabelRow
                    key={fi.index}
                    index={fi.index}
                    shortName={fi.shortName}
                    longName={fi.longName}
                    onSave={(index, shortName, longName) =>
                      onCommand({
                        action: "label_fairlight_input",
                        inputIndex: index,
                        shortName,
                        longName,
                      })
                    }
                    disabled={disabled}
                    maxShortLength={8}
                  />
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-3">
      {renderGroup("MADI 1", madi1)}
      {renderGroup("MADI 2", madi2)}
    </div>
  );
};

/* Audio Routing sub-section — maps audio sources to outputs with channel pair support */
const AudioRoutingSection = ({
  audio,
  inputs,
  onCommand,
  disabled,
}: AudioPanelProps) => {
  const [selectedOutput, setSelectedOutput] = useState(0);
  const routingOutputs = audio.audioRouting ?? [];
  const currentOutput = routingOutputs[selectedOutput];
  const fairlightInputs = useMemo(
    () => audio.fairlightInputs ?? [],
    [audio.fairlightInputs],
  );

  // Build a flat list of all available source pairs for the channel pair routing combobox
  const allSourcePairs = useMemo(
    () => buildAudioSourcePairs(fairlightInputs, inputs),
    [fairlightInputs, inputs],
  );

  if (routingOutputs.length === 0) {
    return <EmptyState message="No audio routing data available" />;
  }

  const isMadi = currentOutput?.sourceType === "madi";
  const hasChannelPairs =
    currentOutput &&
    currentOutput.channelPairs &&
    currentOutput.channelPairs.length > 1;

  return (
    <div className="space-y-3">
      {/* Output selector */}
      <Card>
        <CardHeader className="pb-2 pt-3 px-3">
          <CardTitle className="text-sm">Audio Output Routing</CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <Select
            value={String(selectedOutput)}
            onValueChange={(val) => setSelectedOutput(Number(val))}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {routingOutputs.map((output, idx) => (
                <SelectItem key={idx} value={String(idx)} className="text-xs">
                  {output.name}
                  {(output.channelPairs?.length ?? 0) > 1
                    ? ` (${output.channelPairs.length} pairs)`
                    : ` (${output.sourceType})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Channel pair routing — for MADI and multi-pair outputs */}
      {currentOutput && hasChannelPairs && (
        <Card>
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="text-sm">
              {currentOutput.name} — Channel Pairs (
              {currentOutput.channelPairs.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 space-y-1">
            <p className="text-xs text-muted-foreground mb-2">
              {isMadi
                ? "Map each MADI output channel pair to an audio source pair."
                : currentOutput.sourceType === "aux"
                  ? "SDI embedded audio — map each stereo pair to a source."
                  : "Map each output channel pair to a source."}
            </p>
            <ScrollArea
              className={cn(currentOutput.channelPairs.length > 8 && "h-64")}
            >
              <div className="space-y-1">
                {currentOutput.channelPairs.map((pair) => (
                  <div
                    key={pair.pairIndex}
                    className="flex items-center gap-2 py-0.5"
                  >
                    <span className="text-[10px] font-mono w-8 text-muted-foreground shrink-0">
                      {pair.pairIndex + 1}
                    </span>
                    <Switch
                      checked={pair.enabled}
                      onCheckedChange={(enabled) =>
                        onCommand({
                          action: "set_audio_routing_channel_pair",
                          outputIndex: currentOutput.index,
                          pairIndex: pair.pairIndex,
                          sourceInputIndex: pair.sourceInputIndex,
                          sourceId: pair.sourceId,
                          enabled,
                        })
                      }
                      disabled={disabled}
                    />
                    <AudioSourceCombobox
                      sources={allSourcePairs}
                      value={`${pair.sourceInputIndex}:${pair.sourceId}`}
                      onSelect={(inIdx, srcId) =>
                        onCommand({
                          action: "set_audio_routing_channel_pair",
                          outputIndex: currentOutput.index,
                          pairIndex: pair.pairIndex,
                          sourceInputIndex: inIdx,
                          sourceId: srcId,
                          enabled: pair.enabled,
                        })
                      }
                      disabled={disabled}
                      className="flex-1 min-w-0"
                    />
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Single-pair outputs (Program, Monitor, Headphone) — show the one channel pair */}
      {currentOutput &&
        !hasChannelPairs &&
        currentOutput.channelPairs.length === 1 && (
          <Card>
            <CardHeader className="pb-2 pt-3 px-3">
              <CardTitle className="text-sm">
                {currentOutput.name} — Audio Source
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3">
              {(() => {
                const pair = currentOutput.channelPairs[0];
                return (
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={pair.enabled}
                      onCheckedChange={(enabled) =>
                        onCommand({
                          action: "set_audio_routing_channel_pair",
                          outputIndex: currentOutput.index,
                          pairIndex: pair.pairIndex,
                          sourceInputIndex: pair.sourceInputIndex,
                          sourceId: pair.sourceId,
                          enabled,
                        })
                      }
                      disabled={disabled}
                    />
                    <AudioSourceCombobox
                      sources={allSourcePairs}
                      value={`${pair.sourceInputIndex}:${pair.sourceId}`}
                      onSelect={(inIdx, srcId) =>
                        onCommand({
                          action: "set_audio_routing_channel_pair",
                          outputIndex: currentOutput.index,
                          pairIndex: pair.pairIndex,
                          sourceInputIndex: inIdx,
                          sourceId: srcId,
                          enabled: pair.enabled,
                        })
                      }
                      disabled={disabled}
                      className="flex-1"
                    />
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* MultiView Panel                                              */
/* ─────────────────────────────────────────────────────────── */

interface MultiViewPanelProps {
  multiViews: AtemMultiView[];
  inputs: AtemInput[];
  onCommand: (cmd: AtemCommand) => void;
  disabled?: boolean;
}

const MV_LAYOUTS = [
  { value: 0, label: "Top/Bottom" },
  { value: 1, label: "Left/Right" },
  { value: 2, label: "PGM Large" },
  { value: 3, label: "PVW Large" },
];

const MultiViewPanel = ({
  multiViews,
  inputs,
  onCommand,
  disabled,
}: MultiViewPanelProps) => {
  const [selectedMV, setSelectedMV] = useState(0);

  if (multiViews.length === 0)
    return <EmptyState message="No multi-viewers available" />;

  const mv = multiViews[selectedMV];

  return (
    <div className="space-y-3">
      {/* MV Selector */}
      {multiViews.length > 1 && (
        <div className="flex gap-1">
          {multiViews.map((_, i) => (
            <Button
              key={i}
              variant={selectedMV === i ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setSelectedMV(i)}
            >
              MV {i + 1}
            </Button>
          ))}
        </div>
      )}

      {/* Settings */}
      <Card>
        <CardHeader className="pb-2 pt-3 px-3">
          <CardTitle className="text-sm">
            Multi-Viewer {selectedMV + 1}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3 space-y-2">
          {/* Layout */}
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground w-16">Layout</Label>
            <div className="flex gap-1">
              {MV_LAYOUTS.map((l) => (
                <Button
                  key={l.value}
                  variant={mv.layout === l.value ? "default" : "outline"}
                  size="sm"
                  className="h-6 text-[10px] px-2"
                  onClick={() =>
                    onCommand({
                      action: "set_multiview_layout",
                      mvIndex: mv.index,
                      layout: l.value,
                    })
                  }
                  disabled={disabled}
                >
                  {l.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={mv.vuMetersEnabled ?? false}
                onCheckedChange={(enabled) =>
                  onCommand({
                    action: "set_multiview_vu_meters",
                    mvIndex: mv.index,
                    enabled,
                  })
                }
                disabled={disabled}
              />
              <Label className="text-xs">VU Meters</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={mv.programPreviewSwapped ?? false}
                onCheckedChange={(swapped) =>
                  onCommand({
                    action: "set_multiview_program_preview_swapped",
                    mvIndex: mv.index,
                    swapped,
                  })
                }
                disabled={disabled}
              />
              <Label className="text-xs">Swap PGM/PVW</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Window Grid */}
      <Card>
        <CardHeader className="pb-2 pt-3 px-3">
          <CardTitle className="text-sm">Windows</CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <div className="grid grid-cols-4 gap-2">
            {mv.windows.map((win) => {
              const currentInput = inputs.find((i) => i.index === win.source);
              return (
                <div key={win.index} className="space-y-1">
                  <span className="text-[10px] font-mono text-muted-foreground">
                    {win.index + 1}
                  </span>
                  <SourceCombobox
                    inputs={inputs}
                    value={win.source}
                    onSelect={(source) =>
                      onCommand({
                        action: "set_multiview_window_source",
                        mvIndex: mv.index,
                        windowIndex: win.index,
                        source,
                      })
                    }
                    disabled={disabled}
                    className="w-full"
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Media Panel — Media Players + Pool                          */
/* ─────────────────────────────────────────────────────────── */

interface MediaPanelProps {
  mediaPlayers: AtemMediaPlayer[];
  mediaPool?: AtemState["mediaPool"];
  onCommand: (cmd: AtemCommand) => void;
  disabled?: boolean;
}

const MediaPanel = ({
  mediaPlayers,
  mediaPool,
  onCommand,
  disabled,
}: MediaPanelProps) => {
  if (mediaPlayers.length === 0)
    return <EmptyState message="No media players available" />;

  const stills = mediaPool?.stills?.filter((s) => s.isUsed) ?? [];
  const clips = mediaPool?.clips?.filter((c) => c.isUsed) ?? [];

  return (
    <div className="space-y-3">
      {mediaPlayers.map((mp) => (
        <Card key={mp.index}>
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="text-sm">
              Media Player {mp.index + 1}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 space-y-2">
            {/* Source type & index */}
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                <Button
                  variant={mp.sourceType === "still" ? "default" : "outline"}
                  size="sm"
                  className="h-6 text-[10px]"
                  onClick={() =>
                    onCommand({
                      action: "set_media_player_source",
                      mpIndex: mp.index,
                      sourceType: "still",
                      sourceIndex: mp.sourceIndex,
                    })
                  }
                  disabled={disabled}
                >
                  Still
                </Button>
                <Button
                  variant={mp.sourceType === "clip" ? "default" : "outline"}
                  size="sm"
                  className="h-6 text-[10px]"
                  onClick={() =>
                    onCommand({
                      action: "set_media_player_source",
                      mpIndex: mp.index,
                      sourceType: "clip",
                      sourceIndex: mp.sourceIndex,
                    })
                  }
                  disabled={disabled}
                >
                  Clip
                </Button>
              </div>

              {/* Source selector */}
              <Select
                value={String(mp.sourceIndex)}
                onValueChange={(val) =>
                  onCommand({
                    action: "set_media_player_source",
                    mpIndex: mp.index,
                    sourceType: mp.sourceType,
                    sourceIndex: Number(val),
                  })
                }
                disabled={disabled}
              >
                <SelectTrigger className="h-7 text-xs flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(mp.sourceType === "still" ? stills : clips).map((item) => (
                    <SelectItem
                      key={item.index}
                      value={String(item.index)}
                      className="text-xs"
                    >
                      {item.name ||
                        `${mp.sourceType === "still" ? "Still" : "Clip"} ${item.index + 1}`}
                    </SelectItem>
                  ))}
                  {(mp.sourceType === "still" ? stills : clips).length ===
                    0 && (
                    <SelectItem
                      value={String(mp.sourceIndex)}
                      className="text-xs"
                    >
                      {mp.sourceType === "still" ? "Still" : "Clip"}{" "}
                      {mp.sourceIndex + 1}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Playback controls (clips only) */}
            {mp.sourceType === "clip" && (
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() =>
                    onCommand({
                      action: "set_media_player_playing",
                      mpIndex: mp.index,
                      playing: !mp.playing,
                    })
                  }
                  disabled={disabled}
                >
                  {mp.playing ? (
                    <Pause className="h-3 w-3" />
                  ) : (
                    <Play className="h-3 w-3" />
                  )}
                </Button>
                <Button
                  variant={mp.loop ? "secondary" : "outline"}
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() =>
                    onCommand({
                      action: "set_media_player_loop",
                      mpIndex: mp.index,
                      loop: !mp.loop,
                    })
                  }
                  disabled={disabled}
                >
                  <Repeat className="h-3 w-3" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Media Pool summary */}
      {mediaPool && (
        <Card>
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="text-sm">Media Pool</CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="text-xs text-muted-foreground space-y-1">
              <p>
                Stills: {stills.length} / {mediaPool.stills?.length ?? 0} used
              </p>
              <p>
                Clips: {clips.length} / {mediaPool.clips?.length ?? 0} used
              </p>
            </div>
            {stills.length > 0 && (
              <div className="mt-2 space-y-0.5">
                {stills.map((s) => (
                  <div
                    key={s.index}
                    className="text-xs flex items-center gap-2"
                  >
                    <span className="font-mono text-muted-foreground w-6">
                      {s.index + 1}
                    </span>
                    <span>{s.name}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* SuperSource Panel                                            */
/* ─────────────────────────────────────────────────────────── */

interface SuperSourcePanelProps {
  superSources: AtemSuperSource[];
  inputs: AtemInput[];
  onCommand: (cmd: AtemCommand) => void;
  disabled?: boolean;
}

const SuperSourcePanel = ({
  superSources,
  inputs,
  onCommand,
  disabled,
}: SuperSourcePanelProps) => {
  const [selectedSS, setSelectedSS] = useState(0);

  if (superSources.length === 0)
    return <EmptyState message="No SuperSource available" />;

  const ss = superSources[selectedSS];

  return (
    <div className="space-y-3">
      {superSources.length > 1 && (
        <div className="flex gap-1">
          {superSources.map((_, i) => (
            <Button
              key={i}
              variant={selectedSS === i ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setSelectedSS(i)}
            >
              SSrc {i + 1}
            </Button>
          ))}
        </div>
      )}

      {/* Art (Background) */}
      <Card>
        <CardHeader className="pb-2 pt-3 px-3">
          <CardTitle className="text-sm">Art (Background)</CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground w-8">Fill</Label>
              <SourceCombobox
                inputs={inputs}
                value={ss.artFillSource}
                onSelect={(source) =>
                  onCommand({
                    action: "set_supersource_art",
                    ssIndex: ss.index,
                    fillSource: source,
                    keySource: ss.artKeySource,
                  })
                }
                disabled={disabled}
                className="flex-1"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground w-8">Key</Label>
              <SourceCombobox
                inputs={inputs}
                value={ss.artKeySource}
                onSelect={(source) =>
                  onCommand({
                    action: "set_supersource_art",
                    ssIndex: ss.index,
                    fillSource: ss.artFillSource,
                    keySource: source,
                  })
                }
                disabled={disabled}
                className="flex-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Boxes */}
      {ss.boxes.map((box) => (
        <Card key={box.index}>
          <CardHeader className="pb-2 pt-3 px-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Box {box.index + 1}</CardTitle>
              <Switch
                checked={box.enabled}
                onCheckedChange={(enabled) =>
                  onCommand({
                    action: "set_supersource_box",
                    ssIndex: ss.index,
                    boxIndex: box.index,
                    updates: { enabled },
                  })
                }
                disabled={disabled}
              />
            </div>
          </CardHeader>
          {box.enabled && (
            <CardContent className="px-3 pb-3 space-y-2">
              {/* Source */}
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground w-12">
                  Source
                </Label>
                <SourceCombobox
                  inputs={inputs}
                  value={box.source}
                  onSelect={(source) =>
                    onCommand({
                      action: "set_supersource_box",
                      ssIndex: ss.index,
                      boxIndex: box.index,
                      updates: { source },
                    })
                  }
                  disabled={disabled}
                  className="flex-1"
                />
              </div>

              {/* Position & Size */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] text-muted-foreground">X</Label>
                  <Input
                    type="number"
                    value={box.x}
                    onChange={(e) =>
                      onCommand({
                        action: "set_supersource_box",
                        ssIndex: ss.index,
                        boxIndex: box.index,
                        updates: { x: Number(e.target.value) },
                      })
                    }
                    className="h-7 text-xs"
                    disabled={disabled}
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Y</Label>
                  <Input
                    type="number"
                    value={box.y}
                    onChange={(e) =>
                      onCommand({
                        action: "set_supersource_box",
                        ssIndex: ss.index,
                        boxIndex: box.index,
                        updates: { y: Number(e.target.value) },
                      })
                    }
                    className="h-7 text-xs"
                    disabled={disabled}
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">
                    Size
                  </Label>
                  <Input
                    type="number"
                    value={box.size}
                    onChange={(e) =>
                      onCommand({
                        action: "set_supersource_box",
                        ssIndex: ss.index,
                        boxIndex: box.index,
                        updates: { size: Number(e.target.value) },
                      })
                    }
                    className="h-7 text-xs"
                    disabled={disabled}
                  />
                </div>
              </div>

              {/* Crop */}
              <div className="flex items-center gap-2">
                <Switch
                  checked={box.cropped}
                  onCheckedChange={(cropped) =>
                    onCommand({
                      action: "set_supersource_box",
                      ssIndex: ss.index,
                      boxIndex: box.index,
                      updates: { cropped },
                    })
                  }
                  disabled={disabled}
                />
                <Label className="text-xs">Crop</Label>
              </div>
              {box.cropped && (
                <div className="grid grid-cols-4 gap-1">
                  {(
                    ["cropTop", "cropBottom", "cropLeft", "cropRight"] as const
                  ).map((field) => (
                    <div key={field}>
                      <Label className="text-[9px] text-muted-foreground capitalize">
                        {field.replace("crop", "")}
                      </Label>
                      <Input
                        type="number"
                        value={box[field]}
                        onChange={(e) =>
                          onCommand({
                            action: "set_supersource_box",
                            ssIndex: ss.index,
                            boxIndex: box.index,
                            updates: { [field]: Number(e.target.value) },
                          })
                        }
                        className="h-6 text-[10px]"
                        disabled={disabled}
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Color Generator Panel                                        */
/* ─────────────────────────────────────────────────────────── */

interface ColorGenPanelProps {
  colorGenerators: AtemColorGenerator[];
  onCommand: (cmd: AtemCommand) => void;
  disabled?: boolean;
}

const ColorGenPanel = ({
  colorGenerators,
  onCommand,
  disabled,
}: ColorGenPanelProps) => {
  if (colorGenerators.length === 0)
    return <EmptyState message="No color generators available" />;

  return (
    <div className="space-y-3">
      {colorGenerators.map((cg) => {
        // Convert HSL to a preview CSS color
        const h = cg.hue / 10; // 0-3599 → 0-359.9
        const s = cg.saturation / 10; // 0-1000 → 0-100
        const l = cg.luminance / 10; // 0-1000 → 0-100

        return (
          <Card key={cg.index}>
            <CardHeader className="pb-2 pt-3 px-3">
              <div className="flex items-center gap-2">
                <CardTitle className="text-sm">Color {cg.index + 1}</CardTitle>
                <div
                  className="h-5 w-8 rounded border"
                  style={{ backgroundColor: `hsl(${h}, ${s}%, ${l}%)` }}
                />
              </div>
            </CardHeader>
            <CardContent className="px-3 pb-3 space-y-2">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground w-8">
                    Hue
                  </Label>
                  <Slider
                    value={[cg.hue]}
                    min={0}
                    max={3599}
                    step={1}
                    onValueChange={([val]) =>
                      onCommand({
                        action: "set_color_generator",
                        index: cg.index,
                        hue: val,
                        saturation: cg.saturation,
                        luminance: cg.luminance,
                      })
                    }
                    disabled={disabled}
                    className="flex-1"
                  />
                  <span className="text-[10px] font-mono w-10 text-right">
                    {(cg.hue / 10).toFixed(1)}°
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground w-8">
                    Sat
                  </Label>
                  <Slider
                    value={[cg.saturation]}
                    min={0}
                    max={1000}
                    step={1}
                    onValueChange={([val]) =>
                      onCommand({
                        action: "set_color_generator",
                        index: cg.index,
                        hue: cg.hue,
                        saturation: val,
                        luminance: cg.luminance,
                      })
                    }
                    disabled={disabled}
                    className="flex-1"
                  />
                  <span className="text-[10px] font-mono w-10 text-right">
                    {(cg.saturation / 10).toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground w-8">
                    Lum
                  </Label>
                  <Slider
                    value={[cg.luminance]}
                    min={0}
                    max={1000}
                    step={1}
                    onValueChange={([val]) =>
                      onCommand({
                        action: "set_color_generator",
                        index: cg.index,
                        hue: cg.hue,
                        saturation: cg.saturation,
                        luminance: val,
                      })
                    }
                    disabled={disabled}
                    className="flex-1"
                  />
                  <span className="text-[10px] font-mono w-10 text-right">
                    {(cg.luminance / 10).toFixed(1)}%
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Macro Panel                                                  */
/* ─────────────────────────────────────────────────────────── */

interface MacroPanelProps {
  macros: AtemMacro[];
  onCommand: (cmd: AtemCommand) => void;
  disabled?: boolean;
}

const MacroPanel = ({ macros, onCommand, disabled }: MacroPanelProps) => {
  const usedMacros = macros.filter((m) => m.isUsed);
  const runningMacro = macros.find((m) => m.isRunning);

  if (usedMacros.length === 0)
    return <EmptyState message="No macros configured" />;

  return (
    <div className="space-y-3">
      {runningMacro && (
        <Card className="border-yellow-500/50">
          <CardContent className="py-2 px-3 flex items-center justify-between">
            <span className="text-xs">
              Running:{" "}
              <strong>
                {runningMacro.name || `Macro ${runningMacro.index + 1}`}
              </strong>
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-6 text-[10px]"
              onClick={() => onCommand({ action: "stop_macro" })}
              disabled={disabled}
            >
              Stop
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2 pt-3 px-3">
          <CardTitle className="text-sm">
            Macros ({usedMacros.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
            {usedMacros.map((macro) => (
              <button
                key={macro.index}
                className={cn(
                  "flex flex-col items-start p-2 rounded-md border text-left transition-colors",
                  macro.isRunning
                    ? "bg-yellow-600 text-white border-yellow-600"
                    : "bg-card hover:bg-accent border-border",
                  disabled && "opacity-50 cursor-not-allowed",
                )}
                onClick={() =>
                  onCommand({ action: "run_macro", macroIndex: macro.index })
                }
                disabled={disabled}
                title={macro.description}
              >
                <span className="text-[10px] font-mono text-muted-foreground">
                  {macro.index + 1}
                </span>
                <span className="text-[11px] font-medium truncate w-full">
                  {macro.name || `Macro ${macro.index + 1}`}
                </span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Input Labels Panel                                           */
/* ─────────────────────────────────────────────────────────── */

interface InputLabelsPanelProps {
  inputs: AtemInput[];
  onCommand: (cmd: AtemCommand) => void;
  disabled?: boolean;
}

const InputLabelsPanel = ({
  inputs,
  onCommand,
  disabled,
}: InputLabelsPanelProps) => (
  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="text-base">Input Labels</CardTitle>
    </CardHeader>
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">#</TableHead>
            <TableHead className="w-28">Short Name</TableHead>
            <TableHead>Long Name</TableHead>
            <TableHead className="w-24">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {inputs.map((input) => (
            <AtemLabelRow
              key={input.index}
              index={input.index}
              shortName={input.shortName}
              longName={input.longName}
              onSave={(index, shortName, longName) =>
                onCommand({ action: "label_input", index, shortName, longName })
              }
              disabled={disabled}
            />
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

/* ─────────────────────────────────────────────────────────── */
/* ATEM Label Row - Editable shortName + longName              */
/* ─────────────────────────────────────────────────────────── */

interface AtemLabelRowProps {
  index: number;
  shortName: string;
  longName: string;
  onSave?: (index: number, shortName: string, longName: string) => void;
  disabled?: boolean;
  maxShortLength?: number;
}

const AtemLabelRow = ({
  index,
  shortName,
  longName,
  onSave,
  disabled,
  maxShortLength = 4,
}: AtemLabelRowProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editShort, setEditShort] = useState(shortName);
  const [editLong, setEditLong] = useState(longName);

  const handleSave = () => {
    if (editShort !== shortName || editLong !== longName) {
      onSave?.(index, editShort, editLong);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditShort(shortName);
    setEditLong(longName);
    setIsEditing(false);
  };

  return (
    <TableRow>
      <TableCell className="font-mono text-muted-foreground">
        {index < 1000
          ? index + 1
          : index >= 3101
            ? index - 3100
            : index >= 3001
              ? index - 3000
              : index}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <Input
            value={editShort}
            onChange={(e) =>
              setEditShort(e.target.value.slice(0, maxShortLength))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") handleCancel();
            }}
            className="h-8 w-20 font-mono"
            maxLength={maxShortLength}
            autoFocus
          />
        ) : (
          <span className="font-mono">{shortName}</span>
        )}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <Input
            value={editLong}
            onChange={(e) => setEditLong(e.target.value.slice(0, 20))}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") handleCancel();
            }}
            className="h-8"
            maxLength={20}
          />
        ) : (
          <span>{longName}</span>
        )}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={handleSave}
              disabled={disabled}
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon-sm" onClick={handleCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setIsEditing(true)}
            disabled={disabled || !onSave}
          >
            <Edit2 className="h-4 w-4" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};
